/**
 * useForexPaperAccount — fetches the OANDA forex paper bundle from the
 * Phase 11 read-only bridge and polls it on the same cadence as the Alpaca
 * shared accounts.
 *
 * Phase 11 invariants surfaced through this hook:
 *   - Returns LIVE_LIMITED / LIVE_FULL as 'BLOCKED' (consumers must not
 *     render any control that would unlock either).
 *   - The hook never sees the OANDA API token; the bridge only reports
 *     `oanda_configured: boolean`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchForexBundle, ForexBundle } from '../services/forexService';

const POLL_INTERVAL_MS = 10_000;

export interface UseForexPaperAccountResult {
  bundle: ForexBundle | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * @param enabled - When false, the hook does not fetch or poll. The
 *                  Simulation Lab uses this to avoid polling when the
 *                  forex paper account is not the selected card.
 */
export function useForexPaperAccount(enabled: boolean = true): UseForexPaperAccountResult {
  const [bundle, setBundle] = useState<ForexBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const firstLoadRef = useRef(true);

  const load = useCallback(async () => {
    if (!enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (firstLoadRef.current) setLoading(true);
    setError(null);
    try {
      const next = await fetchForexBundle();
      setBundle(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load forex bundle';
      if (firstLoadRef.current) setError(msg);
      // Don't clobber a good render with a transient poll error.
      // eslint-disable-next-line no-console
      console.error('useForexPaperAccount error:', msg);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
      inFlightRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      load();
    } else {
      setLoading(false);
      setError(null);
    }
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, load]);

  return { bundle, loading, error, refetch: load };
}
